import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  getDocs,
  arrayUnion,
  arrayRemove,
  Timestamp,
  writeBatch,
  onSnapshot,
  limit,
  startAfter,
  DocumentSnapshot,
  increment,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import { db, storage, auth } from '../utils/firebaseConfig';
import { Event, eventService } from './eventService';

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  bio?: string;
  role?: 'user' | 'admin'; // User role for permissions
  favoriteEvents?: string[]; // Array of event IDs
  followersCount?: number;
  followingCount?: number;
  eventsCreatedCount?: number;
  socialLinks?: {
    instagram?: string;
    tiktok?: string;
    x?: string;
    youtube?: string;
  };
  shareEventCreation?: boolean; // Share activities when creating events (default: true)
  shareEventInterested?: boolean; // Share activities when marking interest (default: true)
  shareEventUpdates?: boolean; // Share activities when updating events (default: true)
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface FollowRelationship {
  followerId: string;
  followedId: string;
  createdAt: Timestamp;
}

export interface FollowUser {
  uid: string;
  displayName: string;
  photoURL?: string;
  bio?: string;
  followedAt: Timestamp;
}

class UserService {
  private readonly COLLECTION_NAME = 'users';
  private readonly FOLLOWS_COLLECTION = 'follows';
  private readonly USER_FOLLOWERS_COLLECTION = 'user-followers';
  private readonly USER_FOLLOWING_COLLECTION = 'user-following';
  private readonly STORAGE_PATH = 'user-avatars';

  async getUserProfile(uid: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as UserProfile;
      }

      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw new Error('Failed to fetch user profile');
    }
  }

  async createOrUpdateUserProfile(uid: string, profileData: Partial<UserProfile>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, uid);
      const existingDoc = await getDoc(docRef);

      if (existingDoc.exists()) {
        // Update existing profile
        await updateDoc(docRef, {
          ...profileData,
          updatedAt: Timestamp.now()
        });
      } else {
        // Create new profile
        await setDoc(docRef, {
          uid,
          favoriteEvents: [],
          followersCount: 0,
          followingCount: 0,
          eventsCreatedCount: 0,
          shareEventCreation: true,
          shareEventInterested: true,
          shareEventUpdates: true,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          ...profileData
        });
      }
    } catch (error) {
      console.error('Error creating/updating user profile:', error);
      throw new Error('Failed to update user profile');
    }
  }

  async updateBio(uid: string, bio: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, uid);
      await updateDoc(docRef, {
        bio,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating bio:', error);
      throw new Error('Failed to update bio');
    }
  }

  async updateSocialLinks(uid: string, socialLinks: { instagram?: string; tiktok?: string; x?: string; youtube?: string }): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, uid);
      await updateDoc(docRef, {
        socialLinks,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating social links:', error);
      throw new Error('Failed to update social links');
    }
  }

  async updateActivitySettings(
    uid: string,
    settings: {
      shareEventCreation?: boolean;
      shareEventInterested?: boolean;
      shareEventUpdates?: boolean;
    }
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, uid);
      await updateDoc(docRef, {
        ...settings,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating activity settings:', error);
      throw new Error('Failed to update activity settings');
    }
  }

  async addFavoriteEvent(uid: string, eventId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, uid);
      await updateDoc(docRef, {
        favoriteEvents: arrayUnion(eventId),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error adding favorite event:', error);
      throw new Error('Failed to add favorite event');
    }
  }

  async removeFavoriteEvent(uid: string, eventId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, uid);
      await updateDoc(docRef, {
        favoriteEvents: arrayRemove(eventId),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error removing favorite event:', error);
      throw new Error('Failed to remove favorite event');
    }
  }

  async getUserCreatedEvents(uid: string): Promise<Event[]> {
    try {
      const q = query(
        collection(db, 'events'),
        where('createdBy', '==', uid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Event[];
    } catch (error) {
      console.error('Error fetching user created events:', error);
      throw new Error('Failed to fetch user created events');
    }
  }

  async getUserFavoriteEvents(uid: string): Promise<Event[]> {
    try {
      const userProfile = await this.getUserProfile(uid);
      if (!userProfile?.favoriteEvents || userProfile.favoriteEvents.length === 0) {
        return [];
      }

      // Fetch all favorite events
      const favoriteEvents: Event[] = [];
      
      // Firestore has a limit of 10 items for 'in' queries, so we need to batch
      const batchSize = 10;
      for (let i = 0; i < userProfile.favoriteEvents.length; i += batchSize) {
        const batch = userProfile.favoriteEvents.slice(i, i + batchSize);
        const q = query(
          collection(db, 'events'),
          where('__name__', 'in', batch)
        );

        const snapshot = await getDocs(q);
        const batchEvents = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Event[];

        favoriteEvents.push(...batchEvents);
      }

      // Sort by creation date (newest first)
      return favoriteEvents.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });
    } catch (error) {
      console.error('Error fetching user favorite events:', error);
      throw new Error('Failed to fetch user favorite events');
    }
  }

  async uploadProfilePicture(uid: string, imageFile: File): Promise<string> {
    try {
      const timestamp = Date.now();
      const imageRef = ref(storage, `${this.STORAGE_PATH}/${uid}/${timestamp}_profile.jpg`);
      
      await uploadBytes(imageRef, imageFile);
      const downloadURL = await getDownloadURL(imageRef);
      
      // Update user profile with new photo URL
      await this.createOrUpdateUserProfile(uid, { photoURL: downloadURL });
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw new Error('Failed to upload profile picture');
    }
  }

  async incrementEventsCreatedCount(uid: string): Promise<void> {
    try {
      const userProfile = await this.getUserProfile(uid);
      const newCount = (userProfile?.eventsCreatedCount || 0) + 1;
      
      await this.createOrUpdateUserProfile(uid, { 
        eventsCreatedCount: newCount 
      });
    } catch (error) {
      console.error('Error incrementing events created count:', error);
      throw new Error('Failed to update events created count');
    }
  }

  async isEventFavorited(uid: string, eventId: string): Promise<boolean> {
    try {
      const userProfile = await this.getUserProfile(uid);
      return userProfile?.favoriteEvents?.includes(eventId) || false;
    } catch (error) {
      console.error('Error checking if event is favorited:', error);
      return false;
    }
  }

  // FOLLOWING FUNCTIONALITY

  async followUser(followerId: string, followedId: string): Promise<void> {
    if (followerId === followedId) {
      throw new Error('Cannot follow yourself');
    }

    try {
      const batch = writeBatch(db);
      const followId = `${followerId}_${followedId}`;
      const timestamp = Timestamp.now();

      // Check if already following
      const followDoc = doc(db, this.FOLLOWS_COLLECTION, followId);
      const existingFollow = await getDoc(followDoc);
      if (existingFollow.exists()) {
        throw new Error('Already following this user');
      }

      // 1. Create follow relationship
      batch.set(followDoc, {
        followerId,
        followedId,
        createdAt: timestamp
      });

      // 2. Add to follower's following subcollection
      const followingRef = doc(db, this.USER_FOLLOWING_COLLECTION, followerId, 'following', followedId);
      const followerProfile = await this.getUserProfile(followedId);
      batch.set(followingRef, {
        uid: followedId,
        displayName: followerProfile?.displayName || 'User',
        photoURL: followerProfile?.photoURL || null,
        bio: followerProfile?.bio || null,
        followedAt: timestamp
      });

      // 3. Add to followed user's followers subcollection
      const followersRef = doc(db, this.USER_FOLLOWERS_COLLECTION, followedId, 'followers', followerId);
      const followerUserProfile = await this.getUserProfile(followerId);
      batch.set(followersRef, {
        uid: followerId,
        displayName: followerUserProfile?.displayName || 'User',
        photoURL: followerUserProfile?.photoURL || null,
        bio: followerUserProfile?.bio || null,
        followedAt: timestamp
      });

      // 4. Update follower counts using increment
      const followerRef = doc(db, this.COLLECTION_NAME, followerId);
      batch.update(followerRef, {
        followingCount: increment(1),
        updatedAt: timestamp
      });

      const followedRef = doc(db, this.COLLECTION_NAME, followedId);
      batch.update(followedRef, {
        followersCount: increment(1),
        updatedAt: timestamp
      });

      // Commit all operations atomically
      await batch.commit();
    } catch (error) {
      console.error('Error following user:', error);
      throw new Error('Failed to follow user');
    }
  }

  async unfollowUser(followerId: string, followedId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      const followId = `${followerId}_${followedId}`;
      const timestamp = Timestamp.now();

      // Check if following exists
      const followDoc = doc(db, this.FOLLOWS_COLLECTION, followId);
      const existingFollow = await getDoc(followDoc);
      if (!existingFollow.exists()) {
        throw new Error('Not following this user');
      }

      // 1. Delete follow relationship
      batch.delete(followDoc);

      // 2. Remove from follower's following subcollection
      const followingRef = doc(db, this.USER_FOLLOWING_COLLECTION, followerId, 'following', followedId);
      batch.delete(followingRef);

      // 3. Remove from followed user's followers subcollection
      const followersRef = doc(db, this.USER_FOLLOWERS_COLLECTION, followedId, 'followers', followerId);
      batch.delete(followersRef);

      // 4. Update follower counts using increment (negative)
      const followerRef = doc(db, this.COLLECTION_NAME, followerId);
      batch.update(followerRef, {
        followingCount: increment(-1),
        updatedAt: timestamp
      });

      const followedRef = doc(db, this.COLLECTION_NAME, followedId);
      batch.update(followedRef, {
        followersCount: increment(-1),
        updatedAt: timestamp
      });

      // Commit all operations atomically
      await batch.commit();
    } catch (error) {
      console.error('Error unfollowing user:', error);
      throw new Error('Failed to unfollow user');
    }
  }

  async isFollowing(followerId: string, followedId: string): Promise<boolean> {
    try {
      const followId = `${followerId}_${followedId}`;
      const followDoc = doc(db, this.FOLLOWS_COLLECTION, followId);
      const followSnap = await getDoc(followDoc);
      return followSnap.exists();
    } catch (error) {
      console.error('Error checking follow status:', error);
      return false;
    }
  }

  // Real-time follow status listener
  subscribeToFollowStatus(
    followerId: string, 
    followedId: string, 
    callback: (isFollowing: boolean) => void
  ): () => void {
    const followId = `${followerId}_${followedId}`;
    const followDoc = doc(db, this.FOLLOWS_COLLECTION, followId);
    
    return onSnapshot(followDoc, (doc) => {
      callback(doc.exists());
    }, (error) => {
      console.error('Error in follow status listener:', error);
    });
  }

  // Real-time user profile listener (for follower counts)
  subscribeToUserProfile(
    uid: string, 
    callback: (profile: UserProfile | null) => void
  ): () => void {
    const userDoc = doc(db, this.COLLECTION_NAME, uid);
    
    return onSnapshot(userDoc, (doc) => {
      if (doc.exists()) {
        callback(doc.data() as UserProfile);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in user profile listener:', error);
    });
  }

  async getUserFollowers(uid: string, pageSize: number = 20, lastDoc?: DocumentSnapshot): Promise<{ followers: FollowUser[], lastDoc: DocumentSnapshot | null }> {
    try {
      let followersQuery = query(
        collection(db, this.USER_FOLLOWERS_COLLECTION, uid, 'followers'),
        orderBy('followedAt', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        followersQuery = query(followersQuery, startAfter(lastDoc));
      }

      const snapshot = await getDocs(followersQuery);
      const followers = snapshot.docs.map(doc => doc.data() as FollowUser);
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

      return { followers, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching followers:', error);
      throw new Error('Failed to fetch followers');
    }
  }

  async getUserFollowing(uid: string, pageSize: number = 20, lastDoc?: DocumentSnapshot): Promise<{ following: FollowUser[], lastDoc: DocumentSnapshot | null }> {
    try {
      let followingQuery = query(
        collection(db, this.USER_FOLLOWING_COLLECTION, uid, 'following'),
        orderBy('followedAt', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        followingQuery = query(followingQuery, startAfter(lastDoc));
      }

      const snapshot = await getDocs(followingQuery);
      const following = snapshot.docs.map(doc => doc.data() as FollowUser);
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

      return { following, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching following:', error);
      throw new Error('Failed to fetch following');
    }
  }

  async getFollowCounts(uid: string): Promise<{ followersCount: number, followingCount: number }> {
    try {
      const userProfile = await this.getUserProfile(uid);
      return {
        followersCount: userProfile?.followersCount || 0,
        followingCount: userProfile?.followingCount || 0
      };
    } catch (error) {
      console.error('Error fetching follow counts:', error);
      return { followersCount: 0, followingCount: 0 };
    }
  }

  async deleteUserAccount(uid: string): Promise<void> {
    try {
      const batch = writeBatch(db);

      // 1. Get all events created by the user
      const userEvents = await this.getUserCreatedEvents(uid);

      // 2. Delete all events created by the user
      for (const event of userEvents) {
        if (event.id) {
          await eventService.deleteEvent(event.id);
        }
      }

      // 3. Delete user's follows and followers relationships
      // Get all people this user is following
      const followingQuery = query(
        collection(db, this.USER_FOLLOWING_COLLECTION, uid, 'following')
      );
      const followingSnapshot = await getDocs(followingQuery);

      for (const followDoc of followingSnapshot.docs) {
        const followedId = followDoc.id;
        // Unfollow each person
        try {
          await this.unfollowUser(uid, followedId);
        } catch (error) {
          console.error('Error unfollowing user during account deletion:', error);
        }
      }

      // Get all followers
      const followersQuery = query(
        collection(db, this.USER_FOLLOWERS_COLLECTION, uid, 'followers')
      );
      const followersSnapshot = await getDocs(followersQuery);

      for (const followerDoc of followersSnapshot.docs) {
        const followerId = followerDoc.id;
        // Remove each follower
        try {
          await this.unfollowUser(followerId, uid);
        } catch (error) {
          console.error('Error removing follower during account deletion:', error);
        }
      }

      // 4. Delete user profile from Firestore
      batch.delete(doc(db, this.COLLECTION_NAME, uid));

      // Commit the batch
      await batch.commit();

      // 5. Delete the Firebase Auth user
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === uid) {
        await deleteUser(currentUser);
      } else {
        throw new Error('User authentication mismatch. Please sign in again and try deleting your account.');
      }
    } catch (error) {
      console.error('Error deleting user account:', error);
      throw error;
    }
  }
}

export const userService = new UserService();